import React, { useContext, useEffect, useState } from "react";
import Lottie from "lottie-react";
import { FaArrowRight } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import checkAnimation from "../../assets/images/Success.json";
import { Language } from "../../App";
import { apiAdmin, getSplashData } from "../../services/api";

function CheckBadge() {
  return (
    <div
      className="position-relative d-flex align-items-center justify-content-center mx-auto"
      style={{ width: "150px", height: "150px" }}
    >
      {/* Decorative confetti marks scattered around the badge */}


      {/* Animated teal checkmark badge */}
      <Lottie
        animationData={checkAnimation}
        loop
        style={{ width: "150px", height: "150px" }}
      />
    </div>
  );
}

function FeatureRow({ title, sub }) {
  return (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        borderRadius: "12px",
        padding: "12px",
        border: "1px solid rgba(29, 193, 221, 0.3)",
        backgroundColor: "rgba(29, 193, 221, 0.1)",
      }}
    >
      <span className="fs-13 d-block" style={{ color: "#0E1421", fontWeight: 700 }}>
        {title}
      </span>
      <span className="fs-12 d-block" style={{ color: "#5F6673", fontWeight: 400 }}>
        {sub}
      </span>
    </div>
  );
}

// A function (not a module-level constant) so it rebuilds with the current
// language's translations whenever `lang` changes.
function getFeatures(lang) {
  return [
    {
      title: lang?.mentorship_feature_shape_title || "Shape Future Ophthalmologists",
      sub: lang?.mentorship_feature_shape_sub || "Guide the next generation of eye care specialists.",
    },
    {
      title: lang?.mentorship_feature_reach_title || "Expand Your Global Reach",
      sub: lang?.mentorship_feature_reach_sub || "Connect with ophthalmologists worldwide.",
    },
    {
      title: lang?.mentorship_feature_reputation_title || "Grow Your Professional Reputation",
      sub: lang?.mentorship_feature_reputation_sub || "Be recognized as a trusted clinical mentor.",
    },
    {
      title: lang?.mentorship_feature_flexible_title || "Flexible Online Mentorship",
      sub: lang?.mentorship_feature_flexible_sub || "Mentor from anywhere on your schedule.",
    },
  ];
}

function MentorShipApproved({ isOpen, toggle, onGetStarted }) {
  const { lang } = useContext(Language);
  const features = getFeatures(lang);

  // Same curtain-modal pattern as ApplyMentorShip.jsx / NewCourseDetail2.0.jsx's
  // VideoModal — plain backdrop + content divs animated with @keyframes,
  // not reactstrap's Modal/Fade, so open and close are both immediate and
  // symmetric instead of gated behind a JS timeout.
  const [isClosing, setIsClosing] = useState(false);
  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();
  const handleGetStarted = () => closeThen(() => onGetStarted?.());

  // Mark the sheet as seen the moment it appears, not on a button click —
  // so it never re-shows on the next login even if it's dismissed via
  // backdrop/Escape.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await apiAdmin.post("user/mentor/update", { is_bottom_sheet_view: 1 });
        if (res.data?.status) {
          // Refresh SplashData so the next splash-driven check reads
          // is_bottom_sheet_view: 1 instead of the stale 0.
          const splashRes = await getSplashData();
          if (splashRes.data?.status) {
            localStorage.setItem("SplashData", JSON.stringify(splashRes.data));
          }
        }
      } catch (err) {
        // Non-fatal — the sheet just shows again on the next login.
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`msa-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        /* Same curtain-modal pattern as ApplyMentorShip.jsx — plain
           backdrop + content divs animated with @keyframes, driven by our
           own isClosing state instead of reactstrap's Modal/Fade, so open
           and close are both immediate and symmetric. */
        .msa-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
          animation: msaBackdropFadeIn 0.8s ease-out forwards;
        }
        .msa-curtain-backdrop.closing {
          animation: msaBackdropFadeOut 0.8s ease-out forwards;
        }
        .msa-curtain-content {
          position: relative;
          width: 100%;
          max-width: 539px;
          margin: auto;
          background: #fff;
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.18);
          transform: translateY(-100vh);
          animation: msaCurtainOpen 0.8s ease-out forwards;
        }
        .msa-curtain-content.closing {
          animation: msaCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes msaBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes msaBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes msaCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes msaCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .msa-curtain-backdrop, .msa-curtain-content {
            animation: none !important;
          }
        }
      `}</style>
      <div
        className={`msa-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="position-relative"
          style={{
            padding: "28px 28px 24px",
            background: "linear-gradient(180deg, #DAFAFF 0%, #EFFDFF 10%, #FFFFFF 100%)",
          }}
        >
          {/* Close button */}
          {/* <button
            type="button"
            onClick={handleClose}
            className="position-absolute d-flex align-items-center justify-content-center border-0"
            style={{
              top: "16px",
              right: "16px",
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 2px 8px rgba(15,26,43,0.16)",
            }}
          >
            <IoClose size={20} color="#0E1421" />
          </button> */}

          {/* Badge */}
          <div className="mb-3" style={{ marginTop: "4px" }}>
            <CheckBadge />
          </div>

          {/* Title + subtitle */}
          <h5
            className="text-center mb-2"
            style={{ fontSize: "18px", fontWeight: 700, color: "#0E1421" }}
          >
            {lang?.mentorship_officially_mentor_title || "You're officially a Mentor"}
          </h5>
          <p
            className="text-center mb-4"
            style={{
              fontSize: "13px",
              color: "#5F6673",
              fontWeight: 400,
              lineHeight: "18px",
              padding: "0 12px",
            }}
          >
            {lang?.mentorship_approved_desc || "Congratulations! Your mentor application has been approved. We're excited to have you inspire, guide, and support future ophthalmologists."}
          </p>

          {/* Feature list */}
          <div
            className="d-flex flex-column mb-4 position-relative"
            style={{
              gap: "24px",
              padding: "20px 10px 12px",
              borderRadius: "20px",
              border: "1px solid rgba(29, 193, 221, 0.3)",

              backgroundOrigin: "border-box",
              backgroundClip: "padding-box, border-box",
              overflow: "hidden",
            }}
          >
            {/* Soft ambient glow accent behind the top-left corner */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "-18px",
                left: "-18px",
                width: "56px",
                height: "56px",
                borderRadius: "50%",

                filter: "blur(20px)",
                opacity: 0.15,
                pointerEvents: "none",
              }}
            />
            {features.map((f) => (
              <FeatureRow key={f.title} title={f.title} sub={f.sub} />
            ))}
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={handleGetStarted}
            className="d-flex align-items-center justify-content-center w-100 border-0 text-white"
            style={{
              gap: "8px",
              background: "#00BADA",
              borderRadius: "999px",
              padding: "13px",
              fontWeight: 700,
              fontSize: "14px",
              cursor: "pointer",
              letterSpacing: "0.01em",
            }}
          >
            {lang?.mentorship_lets_get_started_button || "Let's get started"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(MentorShipApproved);